/**
 * Serviço para calcular escalonamento
 * Reutiliza lógica existente de useToolExecution
 */

import { sendToTerminal } from "../../../../lib/terminalLogger";
import { LogCategory } from "../../../../config/logging";
import { APIError } from "../../../../lib/api";
import { QUOTATION_API_URL } from "../../../../config";
import { handleToolsApiError } from "../../../../lib/toolsErrorHandler";
import { apiWithRetry } from "../../../../lib/apiRetry";
import { buildQuotationApiHeaders } from "./quotationApiHeaders";

export interface EscalonamentoRequest {
	proId: number;
	categoriaId: number;
	qualidadeId: number;
	metragem: number;
	produtoUnd?: number;
	contexto?: string;
}

export interface EscalonamentoResult {
	descricao?: string;
	valor_total?: number;
	preco_m2?: number;
	faixa?: string;
	nivel?: string;
	dados?: any;
}

/**
 * Calcula escalonamento para um produto
 */
export async function calculateEscalonamento(
	request: EscalonamentoRequest,
): Promise<EscalonamentoResult> {
	const {
		proId,
		categoriaId,
		qualidadeId,
		metragem,
		produtoUnd = 3,
		contexto = "default",
	} = request;

	// Validações
	if (!proId || proId <= 0) {
		throw new Error("Produto inválido");
	}
	if (!categoriaId || categoriaId <= 0) {
		throw new Error("Categoria inválida");
	}
	if (!qualidadeId || qualidadeId <= 0) {
		throw new Error("Qualidade inválida");
	}
	if (!metragem || metragem <= 0) {
		throw new Error("Metragem inválida");
	}

	const tipoMedida = produtoUnd === 4 ? "UNIT" : "METER";
	// ✅ CORRIGIDO: Construir endpoint relativo (sem /backend)
	// O QUOTATION_API_URL já deve incluir o /backend se necessário
	const endpointPath = `/escalonamento/calcular/${proId}?tipo_medida=${tipoMedida}&categoria_id=${categoriaId}&metragem=${metragem}&qualidade_id=${qualidadeId}&contexto=${contexto}`;

	// ✅ CORRIGIDO: Se QUOTATION_API_URL estiver definida, construir URL completa
	// Caso contrário, usar endpoint relativo (api() vai concatenar com API_URL)
	const endpoint = QUOTATION_API_URL
		? `${QUOTATION_API_URL}${endpointPath}`
		: endpointPath; // URL relativa se QUOTATION_API_URL for vazio
	const willUseDirectFetch =
		!!QUOTATION_API_URL && QUOTATION_API_URL.startsWith("http");

	sendToTerminal(
		"🔍 [QUOTATION] Calculando escalonamento",
		{
			proId,
			categoriaId,
			qualidadeId,
			metragem,
			tipoMedida,
			endpoint,
			quotationApiUrl: QUOTATION_API_URL || "(vazio - usando API_URL)",
			isAbsolute: willUseDirectFetch,
			transport: willUseDirectFetch ? "fetch-direto" : "apiWithRetry-local",
		},
		LogCategory.BUSINESS,
	);

	try {
		// ✅ CORRIGIDO: Se QUOTATION_API_URL estiver definida e for URL absoluta, fazer fetch direto
		// Caso contrário, usar apiWithRetry que vai usar API_URL como base
		let responseData: any;

		if (willUseDirectFetch) {
			// URL absoluta - fazer fetch direto (não usar api() que concatena com API_URL)
			const headers = buildQuotationApiHeaders();
			sendToTerminal(
				"🌐 [QUOTATION] Escalonamento via fetch direto",
				{
					endpoint,
					hasAuthorization: !!headers.Authorization,
					tenantId: headers["X-Tenant-Id"] || "(não enviado)",
					appRegion: headers["x-app-region"] || "(não enviada)",
				},
				LogCategory.API,
			);

			const response = await fetch(endpoint, {
				method: "GET",
				headers,
				mode: "cors",
			});

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new APIError(
					response.status,
					errorData.message || "Erro na requisição",
					"http",
					errorData,
				);
			}

			responseData = await response.json();
		} else {
			// URL relativa - usar apiWithRetry (vai concatenar com API_URL)
			sendToTerminal(
				"🏠 [QUOTATION] Escalonamento via backend local",
				{ endpointPath },
				LogCategory.API,
			);
			responseData = await apiWithRetry(endpointPath, {
				method: "GET",
			});
		}

		// A API retorna { sucesso: true, dados: {...} }
		// Extrair dados do objeto aninhado
		const data = responseData.dados || responseData;

		sendToTerminal(
			"✅ [QUOTATION] Escalonamento calculado",
			{
				sucesso: responseData.sucesso,
				valor_total: data.valor_total,
				preco_m2: data.preco_m2,
				faixa: data.faixa,
				nivel: data.nivel,
				descricao: data.descricao,
			},
			LogCategory.BUSINESS,
		);

		// Retornar no formato esperado pelo código
		return {
			descricao: data.descricao,
			valor_total: data.valor_total,
			preco_m2: data.preco_m2,
			faixa: data.faixa,
			nivel: data.nivel,
			dados: data, // Manter dados completos para compatibilidade
		};
	} catch (error) {
		// ✅ MIGRADO: Usar handleToolsApiError para melhor tratamento
		if (error instanceof APIError) {
			// Criar novo APIError com metadata enriquecido (evitar mutação)
			const enhancedError = new APIError(
				error.status,
				error.message,
				error.kind,
				{ ...error.data, url: endpoint },
			);

			// Converter para AppError usando handler centralizado
			const appError = handleToolsApiError(enhancedError, "escalonamento");

			sendToTerminal(
				"❌ [QUOTATION] Erro ao calcular escalonamento",
				{
					error: appError.message,
					errorCode: appError.code,
					status: error.status,
				},
				LogCategory.ERROR,
			);
		} else {
			const errorMessage =
				error instanceof Error ? error.message : String(error);
			sendToTerminal(
				"❌ [QUOTATION] Erro ao calcular escalonamento",
				{
					error: errorMessage,
				},
				LogCategory.ERROR,
			);
		}

		throw error;
	}
}
